import React, { Fragment, PureComponent } from 'react';
import {
    AerodromeRole,
    CapacityModel,
    CountsAlgorithm,
    CountType,
    RegulationReasonType,
    StandardCountSubtotalsType,
    TRAFFIC_COUNTS_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE,
    TrafficCountsByAerodromeRequestDto,
    TrafficCountsDto,
    TrafficType,
} from 'app/model/CORE';
import AppContext from 'app/common/context/AppContext';
import { ServiceFactory } from 'app/service/ServiceFactory';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';
import { Col, Divider, notification, Row, Select, Space, Spin } from 'antd';
import { Flex } from 'app/components/common/Flex';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import CapacityUtils from 'app/common/util/CapacityUtils';
import { CountsGraph } from 'app/components/panels/tv/graph/CountsGraph';
import TrafficCountUtils from 'app/common/util/TrafficCountUtils';
import TitledBorder from 'app/common/panel/TitledBorder';
import { RegulationOutlook } from 'app/model/SOL49';
import { RegulationPeriods } from 'app/model/client/CORE';
import './RegulationOutlookPanel.less';

interface RegulationOutlookPanelState {
    loading?: boolean;
    lastUpdate?: Date;
    entryCounts?: TrafficCountsDto;
    regulationPeriods?: RegulationPeriods[];
    entryInterval: number;
    entryStep: number;
    entryTrafficType: TrafficType;
}

export class RegulationOutlookPanel extends PureComponent<
    {
        regOutlook: RegulationOutlook;
    },
    RegulationOutlookPanelState
> {
    static REGULATION_OUTLOOK_PANEL_WIDTH_EM = '80em';

    constructor(props: { regOutlook: RegulationOutlook }) {
        super(props);
        this.state = {
            entryInterval: 60,
            entryStep: 60,
            entryTrafficType: TrafficType.LOAD,
        };
    }

    componentDidMount() {
        this.init();
    }

    init = () => {
        this.setState({
            loading: true,
        });

        this.refreshAerodromeCounts()
            .then(entryCountsReply => {
                const regulationPeriods = entryCountsReply?.data.regulatedPeriods?.map(regPeriod => ({
                    calculationType: CountType.ENTRY,
                    constraints: regPeriod.subPeriods?.map(subPeriod => ({
                        timePeriod: subPeriod.period,
                        rate:
                            (subPeriod.normalRate || 0) + (subPeriod.pendingRate || 0) + (subPeriod.equipmentRate || 0),
                    })),
                }));
                this.setState({
                    entryCounts: entryCountsReply?.data,
                    regulationPeriods: regulationPeriods,
                    lastUpdate: AppContext.getCurrentTime(),
                });
            })
            .catch(error => {
                notification.error({ message: 'Error during refresh', description: '' + error, duration: 0 });
                console.log('Error during refresh: ', error);
            })
            .finally(() => this.setState({ loading: false }));
    };

    refreshAerodromeCounts = () => {
        const req: TrafficCountsByAerodromeRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [TrafficType.LOAD, TrafficType.DEMAND, TrafficType.REGULATED_DEMAND],
            trafficWindow: AppContext.getTodayPeriod(),
            algorithm: CountsAlgorithm.NM,
            role: AerodromeRole.GLOBAL,
            aerodrome: this.props.regOutlook.aerodromeId,
            durationInMin: this.state.entryStep,
            intervalInMin: this.state.entryInterval,
            includeForecastFlights: true,
            subtotalsType: StandardCountSubtotalsType.NM,
            computeRegulations: true,
        };
        return ServiceFactory.coreService().getTrafficCounts(req);
    };

    private onEntrySettingsChange = () => {
        this.setState({ loading: true });
        this.refreshAerodromeCounts()
            .then(reply => {
                this.setState({ entryCounts: reply?.data });
            })
            .finally(() => this.setState({ loading: false }));
    };

    render() {
        const worstReason = this.props.regOutlook.reasons?.reduce((prev, curr) =>
            prev.prediction > curr.prediction ? prev : curr,
        )?.reason;
        const predictions = this.props.regOutlook.hourlyPredictions.slice();
        predictions.sort().reverse();
        const highestPred = predictions.filter(pred => pred > 0.75);
        if (Utils.isEmpty(highestPred)) {
            highestPred.push(predictions[0]);
        }
        return (
            <Spin spinning={this.state.loading}>
                <Flex direction="vertical" className="regulation-outlook-panel">
                    <Flex className="modal-title header">
                        <ConditionalRender condition={Utils.isExists(this.state.lastUpdate)}>
                            <Flex className="update-time" size="15em">
                                last update{' '}
                                {TimeUtils.formatDateDDMMYYYY(this.state.lastUpdate, '-') +
                                    ' ' +
                                    TimeUtils.formatHHMM(this.state.lastUpdate)}
                            </Flex>
                        </ConditionalRender>
                        <Flex direction="vertical" className="title">
                            <Flex>Regulation outlook</Flex>
                            <Flex>
                                {`Aerodrome ${this.props.regOutlook.aerodromeId} - ${(this.props.regOutlook.prediction * 100).toFixed(2)}%`}
                            </Flex>
                        </Flex>
                        <Flex grow={false} style={{ justifyContent: 'flex-end' }}>
                            <ConditionalRender condition={this.state.loading}>
                                <LoadingOutlined style={{ fontSize: '2em', paddingRight: '1em' }} />
                            </ConditionalRender>
                            <ConditionalRender condition={!this.state.loading}>
                                <ReloadOutlined
                                    title="Refresh"
                                    onClick={this.init}
                                    style={{ fontSize: '2em', cursor: 'pointer', paddingRight: '1em' }}
                                />
                            </ConditionalRender>
                        </Flex>
                    </Flex>
                    <Divider type="horizontal" />
                    <Flex size="20em" direction="vertical">
                        {this.renderEntry()}
                    </Flex>
                    <Divider type="horizontal" />
                    <Flex style={{ gap: '1em' }}>
                        <TitledBorder title="Predicted reasons" style={{ width: '22em' }}>
                            <Flex direction={'vertical'} style={{ padding: '1em' }}>
                                {this.renderPredictedReasons(worstReason)}
                            </Flex>
                        </TitledBorder>
                        <TitledBorder title="Hourly prediction">
                            {this.renderHourlyPredictions(highestPred)}
                        </TitledBorder>
                    </Flex>
                </Flex>
            </Spin>
        );
    }

    private renderPredictedReasons(worstReason: RegulationReasonType) {
        return this.props.regOutlook.reasons.map(({ reason, prediction }, i) => {
            return (
                <Row
                    gutter={[8, 8]}
                    key={'reasonsOutlook' + i}
                    className={reason === worstReason ? 'worst-reason' : ''}>
                    <Col span={19}>{reason}</Col>
                    <Col span={5}>{`${(prediction * 100).toFixed(0)}%`}</Col>
                </Row>
            );
        });
    }

    private renderHourlyPredictions(highestPred: number[]) {
        return (
            <Flex direction={'vertical'} style={{ padding: '1em' }}>
                {Array(2)
                    .fill(0)
                    .map((_, idx) => {
                        const from = idx * 12;
                        const to = 12 + from;
                        const predictions = this.props.regOutlook.hourlyPredictions.slice(from, to);
                        return (
                            <Fragment key={`hourly-pred-${idx}`}>
                                <Row gutter={[24, 24]}>
                                    {predictions.map((pred, i) => {
                                        const date = TimeUtils.addMinutes(
                                            AppContext.getTodayPeriod().from,
                                            (from + i) * 60,
                                        );
                                        const className = `hourly-pred ${highestPred.includes(pred) ? 'worst-pred' : ''}`;
                                        return (
                                            <Col key={`hourly-pred-${i}`} span={2} className={className}>
                                                <span className={'date'}>{TimeUtils.formatHHMM(date)}</span>
                                                <span className={'prediction'}>{`${(pred * 100).toFixed(1)}%`}</span>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                <ConditionalRender condition={idx === 0}>
                                    <Row>
                                        <Divider type="horizontal" />
                                    </Row>
                                </ConditionalRender>
                            </Fragment>
                        );
                    })}
            </Flex>
        );
    }

    private renderEntry = () => {
        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.state.entryCounts?.hourlyEntryCapacity,
                      this.state.entryCounts?.timeRange,
                  )
                : this.state.entryCounts?.hourlyEntryCapacity;
        return (
            <Flex>
                <Flex size={'60em'}>
                    <CountsGraph
                        currentTime={AppContext.getCurrentTime()}
                        counts={
                            this.state.entryCounts &&
                            TrafficCountUtils.getCountsFor(this.state.entryCounts, this.state.entryTrafficType)
                        }
                        activePeriods={[]}
                        timeRange={this.state.entryCounts?.timeRange}
                        hourlyEntryCapacity={hourlyEntryCapacity}
                        regOutlook={this.props.regOutlook.hourlyPredictions}
                        regulationPeriods={this.state.regulationPeriods}
                        hotspotsPeriods={[]}
                    />
                </Flex>
                <Flex direction="vertical" style={{ paddingLeft: '4em' }}>
                    <Space direction="vertical">
                        <Select value="by_type" style={{ width: '100%' }}>
                            <Select.Option value="by_type">By type</Select.Option>
                            <Select.Option disabled={true} value="by_flow">
                                By flow
                            </Select.Option>
                        </Select>
                        <Select
                            value={this.state.entryTrafficType}
                            onChange={tt => this.setState({ entryTrafficType: tt })}
                            style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                            <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                            <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                        </Select>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                Interval
                                <Select
                                    value={this.state.entryInterval}
                                    onChange={value =>
                                        this.setState({ entryInterval: value }, this.onEntrySettingsChange)
                                    }
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Col>
                            <Col span={12}>
                                Step
                                <Select
                                    value={this.state.entryStep}
                                    onChange={value => this.setState({ entryStep: value }, this.onEntrySettingsChange)}
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Col>
                        </Row>
                    </Space>
                </Flex>
            </Flex>
        );
    };
}
